import moment from 'moment'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiFormat } from '@cbgms/base/utils/datetime'
import { useNotifier } from '@cbgms/base/notifier'
import { Loader } from '@carsys/ui/src/Loader'
import { FORM_CREATE_COMPANY_LICENSE } from 'app-support/company/state/types'
import { getLicenseByUUID, updateLicenseForCompany } from 'app-support/company/state/actions'
import LicenseForm, { IFormValues } from 'app-support/company/components/Licenses/components/LicenseForm'
import { mapLicenseShopIDForAPI } from 'app-support/company/components/Licenses/ShopIDHelper'

const EditLicense = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const { companyUUID, licenseUUID } = useParams<{ companyUUID: string; licenseUUID: string }>()
  const [values, setValues] = useState<IFormValues | undefined>(undefined)
  const [isLoading, setLoading] = useState(true)
  const redirectToURL = `/companies/edit/${companyUUID}#licenses`

  useEffect(() => {
    const asyncReq = async () => {
      try {
        const result = await dispatch(getLicenseByUUID(licenseUUID))
        setValues({ ...result.Data, 'HaynesProLicenseSettings.ClientID': result.Data.LicenseSettings?.ClientID || '' })
      } finally {
        setLoading(false)
      }
    }
    asyncReq()
  }, [licenseUUID])

  const handleSubmit = async (values: IFormValues) => {
    if (!companyUUID) return

    // validFrom is optional, When provided, it should also specify a time.
    const validFrom = moment.utc(values.ValidFrom, apiFormat.yearMonthDay)
    // validTill is optional, The api will reject when validFrom > validTill.
    const validTill = moment.utc(values.ValidTill, apiFormat.yearMonthDay)

    const data = {
      ...values,
      LicenseUUID: licenseUUID,
      CompanyUUID: companyUUID,
      ShopID: mapLicenseShopIDForAPI(values.ShopID),
      ValidFrom: validFrom.isValid() ? validFrom.format(apiFormat.iso) : null,
      ValidTill: validTill.isValid() ? validTill.format(apiFormat.iso) : null
    }
    await dispatch(updateLicenseForCompany(data))

    return redirectToURL
  }
  const onSubmitAndDone = async (values: IFormValues) => {
    await handleSubmit(values)
    return redirectToURL
  }
  const onSubmitSuccess = () => {
    notifier.success(t('companyLicense.edit_success'))
  }

  if (isLoading) {
    return <Loader loading />
  }

  return (
    <LicenseForm
      form={FORM_CREATE_COMPANY_LICENSE}
      handleSubmit={handleSubmit}
      onSubmitAndDone={onSubmitAndDone}
      onSubmitSuccess={onSubmitSuccess}
      license={values}
      redirectToURL={redirectToURL}
    />
  )
}

export default EditLicense
